import {
  Box,
  Typography,
  Sheet,
  Stack,
  FormControl,
  FormLabel,
  Textarea,
  Button,
} from '@mui/joy';
import { FC, FormEvent, JSX, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Input, Loader } from '@consolidocs-ui/components';
import { useToast } from '@consolidocs-ui/utils';
import {
  useGetContractQuery,
  useUpdateContractMutation,
} from '@consolidocs-ui/data-access/ContractApi';
import { Dropdown, PageBreadcrumbs } from '@consolidocs-ui/components';
import { useGetIndustriesQuery } from '@consolidocs-ui/data-access/IndustryApi';
import { IndustryType } from '@consolidocs-ui/types';
import { FieldErrorType, handleApiErrors } from '@consolidocs-ui/utils';
import { ContractFormType } from '@consolidocs-ui/types';
import { contractInitialState } from '../CreateContract/CreateContract';

type ErrorType = {
  data: {
    message: string;
  };
};

export const EditContract: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { contractId } = useParams<{
    contractId: string;
  }>();

  const {
    data: getContract,
    isLoading,
    refetch,
  } = useGetContractQuery(contractId ?? '');

  const [contractForm, setContractForm] =
    useState<ContractFormType>(contractInitialState);

  const [errors, setErrors] = useState<FieldErrorType>({});
  const [updateContract, { isLoading: isLoadingUpdate }] =
    useUpdateContractMutation();
  const [triggerToast] = useToast();
  const { data: industryTypesData, isLoading: isIndustryTypesLoading } =
    useGetIndustriesQuery();
  const industryDropdownOptions =
    industryTypesData?.data?.map((industry: IndustryType) => ({
      label: industry.name,
      value: industry.id,
    })) || [];

  const handleChange = (
    field: keyof ContractFormType,
    value: string | string[]
  ) => {
    setContractForm({ ...contractForm, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newErrors: FieldErrorType = {};

    if (!contractForm?.name.trim()) {
      newErrors.name = 'Contract name is required';
      return setErrors(newErrors);
    }

    try {
      await updateContract(contractForm).unwrap();

      triggerToast({
        message: 'Contract updated successfully',
        type: 'success',
      });

      await refetch();
      navigate('/contracts');
    } catch (err) {
      console.error('Failed to update contract:', err);
      handleApiErrors<ContractFormType>(
        err,
        setErrors,
        (message: string, color: 'success' | 'danger') =>
          triggerToast({ message, type: color }),
        (err as ErrorType)?.data?.message || 'Failed to update contract'
      );
    }
  };

  useEffect(() => {
    if (getContract?.data) {
      const data = getContract.data;

      const normalizedIndustryTypes = Array.isArray(data.industry_types)
        ? data.industry_types.map((item) =>
            typeof item === 'string' ? item : item.id
          )
        : [];

      setContractForm({
        ...data,
        industry_types: normalizedIndustryTypes,
      });
    }
  }, [getContract]);

  return (
    <Box sx={{ flex: 1, width: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 995,
          bgcolor: '#f5f5f5',
          // Cancel the .content wrapper's top padding (p-4) so the bar sticks
          // flush to the top instead of drifting down before it engages.
          mt: { md: -2 },
          pt: { xs: 1, md: 2 },
          pb: 1,
        }}
      >
        <PageBreadcrumbs
          items={[
            {
              label: 'All Contracts',
              path: '/contracts',
            },
            {
              label: 'Edit Contract',
            },
          ]}
        />
      </Box>

      <Sheet
          variant="outlined"
          sx={{
            width: { xs: '100%', sm: '90%', md: '800px' },
            maxWidth: '800px',
            mx: 'auto',
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 'md',
            boxShadow: 'sm',
            borderColor: 'neutral.outlinedBorder',
            marginTop: { xs: '12px', md: '20px' },
          }}
        >
          <Typography
            level="h4"
            mb={2}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            Edit Contract
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mx: 'auto', mt: 4 }}
          >
            {isLoading ? (
              <Loader />
            ) : (
              <Stack spacing={2}>
                <FormControl required>
                  <FormLabel>Contract Code</FormLabel>
                  <Input
                    value={contractForm?.code ?? ''}
                    onChange={(e) => handleChange('code', e.target.value)}
                    placeholder="e.g., MSA-001"
                    required
                    error={!!errors['code']}
                    errorMessage={errors['code']}
                    size="sm"
                  />
                </FormControl>

                <FormControl required>
                  <FormLabel>Contract Name</FormLabel>
                  <Input
                    value={contractForm?.name ?? ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g., Master Service Agreement"
                    required
                    error={!!errors['name']}
                    errorMessage={errors['name']}
                    size="sm"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    minRows={3}
                    value={contractForm?.description ?? ''}
                    onChange={(e) =>
                      handleChange('description', e.target.value)
                    }
                    placeholder="Optional description"
                    size="sm"
                  />
                </FormControl>

                <Dropdown
                  label="Industry Types"
                  multiple
                  options={industryDropdownOptions}
                  value={contractForm.industry_types}
                  onChange={(_, newValue) =>
                    handleChange('industry_types', newValue)
                  }
                  isLoading={isIndustryTypesLoading}
                  error={errors.industry_types}
                />

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    type="button"
                    variant="plain"
                    color="neutral"
                    onClick={() => navigate('/contracts')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="solid"
                    color="primary"
                    loading={isLoadingUpdate}
                    loadingIndicator={<Loader size="sm" />}
                  >
                    Save
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>
        </Sheet>
    </Box>
  );
};
